import User from "../models/User";
import React, {useMemo, useState} from "react";
import {useLocation, useNavigate, useParams, useSearchParams} from "react-router-dom";
import {useMutation, useQueryClient} from '@tanstack/react-query';
import {useProject} from "./ClientProjectProvider";
import {usePosition} from "./ProjectPositionProvider";
import AssignCandidateModal from "../views/project-bidding/components/AssignCandidateModal";
import {useAuth} from "./AuthProvider";
import TalentProvider from "./TalentProvider";


const getCandidate = ({queryKey, signal}) => {
    const {
        profileId,
        project,
        position
    } = queryKey[1] || {};

    let params = {};
    if(project) {
        params.project = project;
    }

    if(position) {
        params.project_position = position;
    }

    return User.fetchTalent(profileId, params, signal)
}


let CandidateContext = React.createContext(null);

const CandidateProvider = ({children, candidate,...props}) => {

    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const location = useLocation();

    const params = useParams();

    const [searchParams, setSearchParams] = useSearchParams();
    const {project, bulkActions, onAddToBulkActions} = useProject();
    const {position} = usePosition();

    const {auth} = useAuth();
    const [isUpdating, setIsUpdating] = useState(false);
    /*
    |--------------------------------------------------------------------------
    | Handling Requests - Add/Remove Team to/from Shortlist/Archive
    |--------------------------------------------------------------------------
    */
    const candidateMutation = useMutation(['candidateRequest', candidate?.id],
        async({method, successKey}) => {

            let updatedCandidate = await candidate[method](
                project, position
            )

            if (updatedCandidate) {
                TB.sysAlert(successKey);
                return updatedCandidate

            }

        }, {
            onError: async (e) => {
                await setIsUpdating(false)
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedCandidate) => {
                await setIsUpdating(false)
                // if (!updatedCandidate) {
                //     TB.showServerErrorToast()
                //     return false;
                // }
            }
        }
    )

    const runProjectTalentRequest = async(method, successKey) => {
        await setIsUpdating(true)

        let updatedCandidate = await candidateMutation.mutateAsync({method, successKey}, {
            onError: async (e) => {
                await setIsUpdating(false)
                return TB.showServerErrorToast()
            },
            onSuccess: async (updatedCandidate) => {
                await setIsUpdating(false)
                // if (!updatedCandidate) {
                //     TB.showServerErrorToast()
                //     return false;
                // }
            }
        });

        await setIsUpdating(false)

        return updatedCandidate
    }

    const addToCandidates = async() => {

        let canAddToCandidates = await project.canShortlistCandidates();
        if (!canAddToCandidates) {
            return;
        }
        let max_candidates = parseInt(laravel.platform_settings.Max_Candidates_Per_BYO_Project_Role) || 10;

        if (position?.total_selected_candidates >= max_candidates) {
            return TB.sysAlert('byo.add_as_candidate_button_clicked_tip_max_candidates')
        }
        let hasSeenTip = await TB.sysAlert('byo.add_as_candidate_button_clicked_tip', {max_candidates})

        if (hasSeenTip) {
            return runProjectTalentRequest(
                'addToCandidates', 'byo.add_as_candidate_button_clicked'
            );
        }
    }

    const removeFromCandidates = async() => {
        return  runProjectTalentRequest(
            'removeFromCandidates', 'byo.remove_from_candidate_button_clicked'
        );
    }

    const addToFavorites = async() => {
        return runProjectTalentRequest(
            'addToFavorites', 'byo.favorites_added'
        );
    }

    const removeFromFavorites = async() => {
        return runProjectTalentRequest(
            'removeFromFavorites', 'byo.favorites_removed'
        );
    }

    const addToLiked = async() => {
        return runProjectTalentRequest(
            'addToLiked', 'byo.shortlist_added'
        );
    }

    const removeFromLiked = async() => {
        return runProjectTalentRequest(
            'removeFromLiked', 'byo.shortlist_removed'
        );
    }

    const addToArchive = async() => {
        return runProjectTalentRequest(
            'addToArchive', 'byo.archive_added'
        );
    }

    const removeFromArchive = async() => {
        return runProjectTalentRequest(
            'removeFromArchive', 'byo.archive_removed'
        );
    }

    const assignCandidate = async() => {
        return TB.event("Modal@show", {
            modalClass: "assign-candidate-modal",
            content: (
                <AssignCandidateModal
                    position={position}
                    candidate={candidate}
                    onConfirm={async () => {
                        if (!candidate.isShortlisted()) {
                            return TB.sysAlert('byo.cant_select_uninterested_talent_for_role');
                        }

                        let updated = await runProjectTalentRequest(
                            'assignToPosition',
                            !TB.has(TB.storedPreferences(), `hide_tips.byo.select_for_role_button_clicked_tip`)
                                ? 'byo.select_for_role_button_clicked_tip'
                                : 'byo.select_for_role_button_clicked');

                        if (updated) {
                            await queryClient.invalidateQueries({
                                predicate: ({queryKey}) => {
                                    const params = queryKey[1] || {};

                                    return queryKey[0] === 'getConversation' &&
                                        params.positionId === position?.id &&
                                        params.candidateId === candidate?.id
                                }
                            })
                        }
                    }}
                />
            )
        })
    }

    const unassignCandidate = async() => {
        if (project?.hasRequestedSOW()) {
            return TB.toast("Can't reopen after SOW has already been requested!", {theme: 'danger'})
        }
        if (position.assigned_candidate?.id !== candidate?.id) {
            return;
        }

        return runProjectTalentRequest('unassignFromPosition', 'byo.removed_for_role_button_clicked');
    }

    const hasProposalForClient = useMemo(() => candidate?.hasProposalForClient(), [
        candidate?.job_candidate?.status,
        candidate?.job_candidate?.client_match_status
    ]);

    const permissionToInvite = project && auth?.canInviteTalentForProject(project);
    const canFavorite = auth?.isClient();

    const canArchive = useMemo(() => {
        return permissionToInvite && (candidate?.job_candidate?.was_disliked || !candidate.disabledFromInvite());
    }, [
        auth,
        candidate?.job_candidate?.was_liked,
        candidate?.job_candidate?.was_disliked,
        candidate?.job_candidate?.is_candidate,
        candidate?.job_candidate?.status,
        position.assigned_candidate?.id,
    ]);

    const canMessageOrInterview = useMemo(() => {
        if (!hasProposalForClient || !candidate.isShortlisted() || (
            project && !auth?.canRequestTalentInterviewForProject(project)
        )) return false;

        return true;
    }, [
        hasProposalForClient,
        candidate?.job_candidate?.is_shortlisted,
        auth
    ]);

    const overrideClientPreferredRateMutation = useMutation(
        ['overrideClientPreferredRate', candidate.id],
        (rate) => {
            return candidate.overrideClientPreferredRate(rate, project,position)
        }, {
            onSuccess: async (result) => {
                console.log('onSuccess', result)

                await TB.wait(300);

                TB.toast("Talent Preferred Rate Set Successfully", {type:'success'})

            },
            onError: error => {
                TB.showServerErrorToast()
            }
        });

    const matchingDisciplines = useMemo(() => {

        if (candidate?.matching?.position_templates && position?.templates){
            return candidate?.matching?.position_templates?.templates || []
        }
        if(!candidate?.matching?.disciplines?.disciplines || !position?.disciplines) {
            return [];
        }
        return candidate.matchedDisciplines(position.disciplines);
    }, [position?.templates,position?.disciplines, candidate?.matching]);

    const isBulkActionsSelected = useMemo(() => {
        return TB.find(bulkActions, ['id', candidate.id])
    }, [bulkActions]);

    let value = {
        candidate,
        addToCandidates,
        removeFromCandidates,
        addToFavorites,
        removeFromFavorites,
        addToLiked,
        removeFromLiked,
        addToArchive,
        removeFromArchive,
        assignCandidate,
        unassignCandidate,
        permissionToInvite,
        canInterview: canMessageOrInterview,
        canMessage: canMessageOrInterview,
        canRunBulkActions: auth.canManageClients(),
        onAddToBulkActions: () => onAddToBulkActions(candidate),
        isBulkActionsSelected,
        canArchive,
        canFavorite,
        matchingDisciplines,
        overrideClientPreferredRate: (rate) => overrideClientPreferredRateMutation.mutateAsync(rate),
        isDisabled: isUpdating || candidateMutation.isLoading || overrideClientPreferredRateMutation?.isLoading,
        showCandidateProposal: async (queryParams = '') => {
            let existingParams = new URLSearchParams(searchParams);

            existingParams.set('tId', candidate.id)
            if (!params?.positionId) {
                existingParams.set('role', position.id);
            }

            return navigate({
                ...location,
                search: `?${existingParams.toString()}${queryParams}`
            }, {replace: true})
        }
    }

    const matchCriteria = useMemo(() => {
        if (!props.criteria) return undefined;

        let criteria = TB.cloneDeep(props.criteria || {});
        if (props.criteria?.skills) {
            let groupedSkills = TB.groupBy(props.criteria.skills, 'importance');
            TB.map(laravel.importance_levels, (prefix, importanceLevel) => {
                criteria[`${TB.snakeCase(prefix)}_skills`] = groupedSkills?.[parseInt(importanceLevel)] || [];
            });
        }
        if (props.criteria?.industries) {
            let groupedSkills = TB.groupBy(props.criteria.industries, 'importance');
            TB.map(laravel.importance_levels, (prefix, importanceLevel) => {
                criteria[`${TB.snakeCase(prefix)}_industries`] = groupedSkills?.[parseInt(importanceLevel)] || [];
            });
        }
        return criteria;
    }, [props.criteria]);

    return (
        <CandidateContext.Provider value={value}>
            <TalentProvider talent={candidate}
                            onUpdated={() => true} criteria={matchCriteria}
                            overrideClientPreferredRate={value.overrideClientPreferredRate}>
                {children}
            </TalentProvider>
        </CandidateContext.Provider>
    );
}

const useCandidate = () => {
    return React.useContext(CandidateContext);
}
export default CandidateProvider;
export {
    useCandidate,
    CandidateContext
}